import React, { useEffect, useState } from 'react'
import cn from 'classnames'
import Button from 'antd/lib/button'
import Alert from 'antd/lib/alert'
import message from 'antd/lib/message'
import { useLocation } from 'react-router-dom'
import PayInvoiceDialog from '../dialogs/pay-invoice'
import StripeConfig from '../core/StripeConfig'
import CoreButton from '../core/CoreButton'
import MaterialIcon from '../core/MaterialIcon'
import { useTheme } from '../core/ThemeProvider/ThemeProvider'
import { URLS } from '../../constants/constants'
import { failedPaymentMessage } from '../../common/failed-payment'
import * as api from '../../services/api'
import styles from './unpaid-banner.module.scss'

export const PAYMENT_FAILED_STATUSES = ['incomplete', 'past_due']

export const openBillingPortal = async () => {
  try {
    const { portalUrl } = await api.subscription.createPortalSession()
    window.location.href = portalUrl
  } catch (error) {
    message.error('Can’t open billing right now. Please try again or contact support.')
  }
}

// The pinned banner publishes its height; the spacer and fixed/100vh layouts
// offset by it. Reads the observed node, not a ref: React nulls refs on
// unmount before effect cleanup.
const useTopBannerHeight = (enabled) => {
  const [node, setNode] = useState(null)
  useEffect(() => {
    if (!enabled || !node || !window.ResizeObserver) return
    const root = document.documentElement
    const observer = new window.ResizeObserver(() => {
      root.style.setProperty('--top-banner-height', `${node.offsetHeight}px`)
    })
    observer.observe(node)
    return () => {
      observer.disconnect()
      root.style.removeProperty('--top-banner-height')
    }
  }, [enabled, node])
  return setNode
}

const PaymentFailedAlert = ({ user }) => {
  const { themeMode } = useTheme()
  return (
    <div role='alert' className={cn(styles.alert, styles[themeMode])}>
      <div className={styles.message}>
        <MaterialIcon name='error' className={styles.icon} />
        <div className={styles.text}>
          <div className={styles.title}>Payment Failed</div>
          <div className={styles.description}>{failedPaymentMessage(user)}</div>
        </div>
      </div>
      <div className={styles.cta}>
        <CoreButton
          size='small'
          onClick={openBillingPortal}
          iconRight={<MaterialIcon name='arrow_forward' variant='label-default' style={{ fontSize: 18 }} />}
        >
          Update Payment
        </CoreButton>
      </div>
    </div>
  )
}

function UnpaidAlert ({ user, isBanner, sticky }) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [isOld, setIsOld] = useState(false)
  const location = useLocation()
  const ref = useTopBannerHeight(sticky)
  useEffect(() => {
    if (URLS.includes(location.pathname)) {
      setIsOld(true)
    }
  }, [])

  // Sticky = pinned to the viewport; the spacer keeps in-flow content below it.
  const frame = (alert) => (
    <>
      <div ref={ref} className={sticky ? 'payment-failed-banner' : ''}>{alert}</div>
      {sticky && <div className='payment-failed-banner-spacer' />}
    </>
  )

  if (user.subscriptionStatus === 'past_due') {
    return frame(<PaymentFailedAlert user={user} />)
  }

  const unpaidInvoiceUrl = user?.subscriptionData?.unpaidInvoice?.hosted_invoice_url
  return (
    <StripeConfig>
      {frame(
        <Alert
          className={isOld ? 'unpaid-banner' : ''}
          showIcon
          description={`You have an incomplete payment. Please pay the invoice by ${user.failedExpiredAt} to avoid cancellation of your ${user.unpaidOrder ? 'order' : 'subscription'}.`}
          type='error'
          banner={isBanner}
          action={
            unpaidInvoiceUrl
              ? (
                <Button type='link' size='small' target='_blank' href={unpaidInvoiceUrl}>Pay Now</Button>
                )
              : (
                <Button size='small' danger onClick={() => setDialogOpen(true)}>
                  Pay Now
                </Button>
                )
          }
        />
      )}
      {dialogOpen &&
        <PayInvoiceDialog
          user={user}
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
        />}
    </StripeConfig>
  )
}

export default UnpaidAlert
